import { strIsNullOrEmpty, tryParseJSONObject } from '@fox/atoms';
import { AppliesToType, formatCurrency, FormFinancialLineItem, StandardLineType } from '@fox/finance';
import { UseQueryResult } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { MutableRefObject } from 'react';
import { TICKET_BLOCK_TYPE } from '../../../form-block-types';
import { ReservationModel, ReservationStatus } from '../../form-event-context';
import { IQuestionAnswer } from '../../form-root';
import { TaxRatesData } from '../payment-block/contracts';
import { ISection } from '../section-block/contracts';
import { TicketAnswer, TicketDefinition, TicketFieldProps } from './contracts';

/**
 * Build the initialProps for a new ticket block when added to form
 * @returns
 */
export const buildTicketInitialProps = (): TicketFieldProps => ({
	required: false,
	labelOverrides: {},
	allowedTickets: [],
	conditionalDefinitions: [],
	name: nanoid(),
	type: TICKET_BLOCK_TYPE,
	allowQuantitySelection: true,
	min: 0,
	max: 10,
	provides: {},
	title: 'Tickets'
});

/**
 * Build the line item of the ticket/s for the answer data if ticket found and has quantity
 * @param availableTickets - Tickets available for selection
 * @param answer - Ticket form answer
 * @returns Line item
 */
export const buildTicketLineItem = (availableTickets: TicketDefinition[], answer: TicketAnswer): FormFinancialLineItem[] | null => {
	const currentTicket = availableTickets?.find(t => t.id === answer?.ticketId);
	if (!currentTicket || !answer?.quantity || (answer?.quantity ?? 0) < 1) return null;
	const toRet: FormFinancialLineItem = {
		amount: currentTicket.price * answer.quantity,
		comment: `${currentTicket.name}`,
		lineTypeId: StandardLineType.Ticket,
		quantity: answer.quantity,
		immediatePayment: true,
		unitAmount: currentTicket.price,
		targetId: currentTicket.id,
		targetType: AppliesToType.Ticket
	};

	if (currentTicket.financial) {
		// NOTE: Will this be better to get definition and add in snf before financial mutation
		// If so then maybe price would be safer to get there too
		toRet.invoice = {
			...currentTicket.financial
		};
	}

	return [toRet];
};

/**
 * Find the number of tickets currently reserved by user
 * @param reservation - The current ticket reservation
 * @param ticketId - Ticket ID to match
 * @returns - The reserved ticket count or 0 if none/cannot find
 */
export const findReservedTicketCount = (reservation?: ReservationModel, ticketId?: string, allowAnyTicket?: boolean) => {
	if (typeof reservation?.count !== 'number' || (!ticketId && !allowAnyTicket)) return 0;
	const reserved = (reservation.fieldId === ticketId || allowAnyTicket) && reservation.status === ReservationStatus.Pending ? reservation.count : 0;
	return reserved;
};

export const calculateRemainingTickets = (
	ticket?: TicketDefinition | null,
	reservation?: ReservationModel,
	quotaRemaining?: number,
	otherQuotaReserved?: number
): number | undefined => {
	if (!ticket?.ticketCounts) return;
	const multiplier = ticket.groupMultiplier ?? 1;
	const reserved = findReservedTicketCount(reservation, ticket?.id) * multiplier;
	const avail = ticket.ticketCounts.limit - ticket.ticketCounts.current;
	const quotaAvailable =
		typeof quotaRemaining === 'number' && typeof otherQuotaReserved === 'number' ? quotaRemaining + otherQuotaReserved : undefined;
	const remaining = Math.floor((Math.min(avail, quotaAvailable ?? avail) + reserved) / multiplier);
	return isNaN(remaining) ? undefined : remaining;
};

export function getPeopleCountFromAnswers(answers: IQuestionAnswer[], sections: ISection[]): number {
	// if form doesn't contain either then assume targeted form with no contact blocks in which case only 1 contact
	const allFields = sections.flatMap(s => s.fields);
	if (!allFields?.some(b => b.type === 'CreatePeople' || b.type === 'CreatePerson')) {
		return 1;
	}

	const isSinglePerson = answers?.find(a => a.type === 'CreatePerson') ?? allFields.some(b => b.type === 'CreatePerson');
	if (isSinglePerson) return 1;
	const multiPerson = answers?.find(a => a.type === 'CreatePeople');
	if (multiPerson) {
		const currentAnswerSelections = tryParseJSONObject<{ contacts?: unknown[] }>(multiPerson?.answer?.answerJson, {});
		const contactsCounts: number = currentAnswerSelections.contacts?.length ?? 0;
		return contactsCounts;
	}

	return 0;
}

/**
 * Calculates the quantity of tickets for when user quantity selection is disabled
 * Quantity is calculated based on number of multi contact people created in form or 1 if only CreatePerson
 * @param answers - Form answers
 * @param currentAnswer - Current ticket answer
 * @param sections - Form sections
 * @returns
 */
export const calculateTicketQuantity = (
	answers: IQuestionAnswer[],
	sections: ISection[],
	ticket?: TicketDefinition,
	reservation?: ReservationModel,
	quotaRemaining?: number,
	otherQuotaReserved?: number // quota reserved by a different ticket
) => {
	const peopleCount = getPeopleCountFromAnswers(answers, sections);
	const multiplier = ticket?.type === 'group' ? (ticket.groupMultiplier ?? 1) : 1;
	const ticketsRemaining = calculateRemainingTickets(ticket, reservation, quotaRemaining, otherQuotaReserved);
	if (peopleCount === 0) return 0;
	if (typeof ticketsRemaining === 'number' && peopleCount >= ticketsRemaining * multiplier) {
		return ticketsRemaining;
	}
	return Math.ceil(peopleCount / multiplier);
};

/**
 * Calculates line item total, returns total amount still as cents
 * Note we have a similar function in snf that you should adjust when making changes here
 * @param lineItems
 * @param taxRates
 * @returns
 */
export const calculateLineItemsTotal = (lineItems: FormFinancialLineItem[], taxRates?: TaxRatesData['rates']) => {
	const total = (lineItems ?? []).reduce((acc, cur) => {
		const defaultTaxRate = taxRates?.find(t => t.default);
		const matchingTaxRate = taxRates?.find(t => t.id === cur.invoice?.taxRate);
		const taxRate = (matchingTaxRate ?? defaultTaxRate)?.rate;
		const calculatedTaxAmount = cur.amount * ((taxRate ?? 0) / 100);
		const taxAmount = cur.invoice?.taxInclusive === false ? calculatedTaxAmount : 0;
		const currentAmount = Math.round(cur.amount + taxAmount);

		acc += currentAmount;
		return acc;
	}, 0);
	return total;
};
export const reserveTickets = async (input: {
	token: string;
	newAnswer: TicketAnswer;
	reservation: MutableRefObject<ReservationModel | undefined>;
	reserveTicketMutation: (input: {
		eventId: string;
		ticketId?: string;
		count?: number;
		reservationId?: string;
		formRequestId: string;
		enforceCount?: boolean;
		token?: string;
	}) => Promise<{ reservation?: ReservationModel; isCapped?: boolean }>;
	eventId: string;
	formRequestId: string;
	refetch?: (options?: { throwOnError: boolean; cancelRefetch: boolean }) => Promise<UseQueryResult>;
	enforceCount?: boolean;
}): Promise<{ isCapped?: boolean; reservation?: ReservationModel }> => {
	const { token, newAnswer, reservation, reserveTicketMutation, eventId, formRequestId, refetch, enforceCount } = input;

	const existingReservation = reservation?.current;
	let reservationResult: { reservation?: ReservationModel; isCapped?: boolean } | undefined = {};

	const canReserveTickets = !strIsNullOrEmpty(newAnswer?.ticketId) && typeof newAnswer?.quantity === 'number' && newAnswer?.quantity > 0;
	if (canReserveTickets) {
		// Case: Create new ticket reservation
		reservationResult = await reserveTicketMutation({
			formRequestId,
			eventId,
			ticketId: newAnswer?.ticketId,
			count: newAnswer?.quantity,
			reservationId: reservation?.current?.id,
			enforceCount,
			token
		});

		// Update quantity to match reservation count (incase amount available is now lower than amount user wanted)
		newAnswer.quantity = reservationResult ? reservationResult.reservation?.count : undefined;
	} else if (reservation?.current?.status === ReservationStatus.Pending) {
		// Case: Existing pending reservation and incomplete answer (e.g. if ticket field cleared)
		await reserveTicketMutation({ eventId, reservationId: reservation?.current?.id, token, formRequestId });
	}
	if (canReserveTickets || existingReservation) {
		await refetch?.();
	}
	if (reservation) {
		reservation.current = reservationResult?.reservation;
	}
	return reservationResult;
};

// calculate additional quota for reservations where res ticket is not selected ticket
export const calculateOtherTicketQuota = (tickets: TicketDefinition[], selectedTicket?: TicketDefinition, reservation?: ReservationModel) => {
	if (reservation && selectedTicket && selectedTicket?.id !== reservation?.fieldId) {
		const resCount = reservation?.count;
		const multiplier = tickets?.find(t => t.id === reservation?.fieldId)?.groupMultiplier ?? 1;
		return resCount * multiplier;
	}
	return 0;
};

export function createOptionLabel(option: TicketDefinition, defaultCurrency: string | undefined) {
	return `${option.name} (${formatCurrency(option.price, defaultCurrency)}${option?.type === 'group' ? ` for ${option.groupMultiplier ?? 1} tickets` : ''})`;
}
